import React, { useState, useRef, useEffect } from 'react';
import { languageShowcase } from '../mock/komalData';
import { Play, X } from 'lucide-react';
import OptimizedImage from './OptimizedImage';

const ShadesGallery = () => {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  // Track scroll position and update active card
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.offsetWidth * 0.85; // 85% width
      const currentIndex = Math.round(scrollLeft / cardWidth);
      setActiveCardIndex(Math.min(Math.max(currentIndex, 0), languageShowcase.length - 1));
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.split('youtu.be/')[1]?.split('?')[0] || url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`;
  };

  return (
    <section id="shades" className="min-h-screen bg-[#1a1a1a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4">
            <span className="text-[#4ECDC4]">VERSATILITY</span>
          </h2>
          <div className="w-24 h-1 bg-[#4ECDC4] mx-auto mb-6"></div>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Fluent across multiple languages and regional dialects. Watch my auditions showcasing 
            authentic cultural portrayals from different regions of India and beyond.
          </p>
        </div>

        {/* Desktop Grid (hidden on mobile) */}
        <div className="hidden lg:grid grid-cols-5 gap-6 max-w-7xl mx-auto">
          {languageShowcase.map((item, index) => (
            <div
              key={index}
              className={`group relative bg-[#0a0a0a] rounded-lg overflow-hidden border border-gray-800 hover:border-[#4ECDC4] transition-all duration-300 ${
                item.auditionUrl ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
              }`}
              onClick={() => item.auditionUrl && setSelectedLanguage(item)}
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <OptimizedImage
                  src={item.photo}
                  alt={item.language}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
                
                {item.auditionUrl && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-[#4ECDC4]/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-[#4ECDC4]">
                      <Play className="text-[#4ECDC4] ml-1" size={28} fill="#4ECDC4" />
                    </div>
                  </div>
                )}
                
                {!item.auditionUrl && (
                  <div className="absolute top-3 right-3 bg-yellow-500/90 text-black text-xs font-bold px-3 py-1 rounded-full">
                    Coming Soon
                  </div>
                )}
                
                <div className="absolute top-4 left-4">
                  <span 
                    className="px-5 py-2 rounded-full font-bold text-sm uppercase tracking-wider shadow-lg"
                    style={{ 
                      backgroundColor: item.color,
                      color: '#FFFFFF',
                      border: '2px solid rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    {item.language}
                  </span>
                </div>
              </div>

              <div className="p-4">
                <h3 className="text-white font-bold text-lg mb-1">{item.language}</h3>
                <p className="text-gray-400 text-xs mb-2">{item.region}</p>
                <p className="text-gray-500 text-xs line-clamp-2">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="lg:hidden">
          <div className="relative max-w-md mx-auto">
            {/* Swipeable Container - showing peek of next card */}
            <div 
              ref={scrollContainerRef}
              className="overflow-x-auto snap-x snap-mandatory scrollbar-hide flex gap-4 pb-4 -mx-6 px-6"
            >
              {languageShowcase.map((item, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-[85%] snap-center"
                  onClick={() => item.auditionUrl && setSelectedLanguage(item)}
                >
                  <div className={`bg-[#0a0a0a] rounded-lg overflow-hidden border-2 ${
                    item.auditionUrl ? 'border-gray-800' : 'border-gray-800 opacity-60'
                  }`}>
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <OptimizedImage
                        src={item.photo}
                        alt={item.language}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                      
                      {item.auditionUrl && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 bg-[#4ECDC4]/30 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-[#4ECDC4]">
                            <Play className="text-[#4ECDC4] ml-1" size={28} fill="#4ECDC4" />
                          </div>
                        </div>
                      )}
                      
                      {!item.auditionUrl && (
                        <div className="absolute top-4 right-4 bg-yellow-500 text-black text-xs font-bold px-3 py-1.5 rounded-full">
                          Coming Soon
                        </div>
                      )}
                      
                      <div className="absolute bottom-4 left-4 right-4">
                        <span 
                          className="inline-block px-6 py-2.5 rounded-full font-bold text-base uppercase tracking-wider shadow-xl"
                          style={{ 
                            backgroundColor: item.color,
                            color: '#FFFFFF',
                            border: '2px solid rgba(255, 255, 255, 0.4)'
                          }}
                        >
                          {item.language}
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-white font-bold text-xl mb-2">{item.language}</h3>
                      <p className="text-[#4ECDC4] text-sm mb-3 font-medium">{item.region}</p>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Active Dot Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {languageShowcase.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === activeCardIndex
                      ? 'bg-[#4ECDC4] w-6'
                      : 'bg-gray-600 w-2'
                  }`}
                ></div>
              ))}
            </div>

            <p className="text-center text-gray-500 text-xs mt-4">
              ← Swipe to explore →
            </p>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedLanguage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <button
            className="absolute top-8 right-8 text-white hover:text-[#4ECDC4] transition-colors z-10"
            onClick={() => setSelectedLanguage(null)}
            aria-label="Close"
          >
            <X size={40} />
          </button>

          <div className="max-w-5xl w-full">
            <div className="relative aspect-video rounded-lg overflow-hidden mb-6 bg-black">
              {selectedLanguage.auditionUrl && (
                <iframe
                  src={getYouTubeEmbedUrl(selectedLanguage.auditionUrl)}
                  title={selectedLanguage.language}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              )}
            </div>

            <div className="text-center">
              <span 
                className="inline-block px-6 py-2 rounded-full font-bold uppercase tracking-wider mb-4"
                style={{ 
                  backgroundColor: `${selectedLanguage.color}20`,
                  color: selectedLanguage.color,
                  border: `2px solid ${selectedLanguage.color}`
                }}
              >
                {selectedLanguage.language}
              </span>
              <h3 className="text-white text-3xl font-bold mb-2">{selectedLanguage.language}</h3>
              <p className="text-[#4ECDC4] text-xl mb-4">{selectedLanguage.region}</p>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">{selectedLanguage.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ShadesGallery;
